import React, { useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import get from 'lodash.get';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { DimensionInput } from 'ui-components/components/dimension-input';
import { SimpleDropdown } from 'ui-components/components/dropdown/simple';
import { WeightInput } from 'ui-components/components/weight-input';

import { messages } from './messages';
import { PackageTypeLabel } from './package-type-label';
import { PackageItemRow } from './row';
import { MULTI_PACKAGE_CUSTOM_PACKAGE_TYPE_SELECTED } from '../../../../constants/analytics-events';
import { CSWFieldId } from '../../../../constants/field-ids';
import { WeightUnit } from '../../../../enums/weight-unit';
import DimensionsLabel from '../../../../fulfillment-planner/dimensions/label';
import InsuredAmountLabel from '../../../../fulfillment-planner/insured-amount/label';
import { PackageContentDescriptionLabel } from '../../../../fulfillment-planner/package-content-description/label';
import WeightLabel from '../../../../fulfillment-planner/weight/label';
import Scale from '../../../../fulfillment-planner/weight/scale';
import { useReadFromScale } from '../../../../hooks/use-read-from-scale';
import { Segment } from '../../../../services/analytics-services/segment';
import { toFixedNumber } from '../../../../services/number-formatting-service';
import { convertFromDimensionUnitEnum } from '../../../../services/unit-services/dimensions/dimension-unit-enum-converter';
import { convertFromWeightUnitEnum, convertToWeightUnitEnum } from '../../../../services/unit-services/weights/weight-unit-enum-converter';
import Global from '../../../../store/global-selectors';
import { formatDimensions, formatWeight, formatCurrency } from '../../../shipment-details/shipment-details-helper';
import { PackageContentDescriptionInputForMultiPackage } from '../package-content-description/input';
import { PackageInsurance } from '../package-insurance';
import style from './package-item.module.less';

/**
 * @typedef {object} PackageItemProps
 * @property {(insuredAmount: number, currencyCode: string, index: number) => void} changeInsuranceValue - Callback to update insurance value
 * @property {(dimensions: object, index: number) => void} changePackageDimensions - Callback to update package dimensions
 * @property {(description: string, index: number) => void} changePackageContentDescription - Callback to update package content description
 * @property {(weight: object, index: number) => void} changeWeight - Callback to update package weight
 * @property {(pack: object) => void} duplicatePackage - Callback to duplicate a package
 * @property {boolean} [hasInsuranceProviderSelected=false] - Whether an insurance provider has been selected
 * @property {number} [index=0] - Index of the package in the list
 * @property {import('../../../../prop-types/package').PackagePropType} pack - Package data object
 * @property {boolean} [readOnly=false] - Whether the component is in read-only mode
 * @property {(index: number) => void} removePackage - Callback to remove a package
 * @property {boolean} [shouldRequirePackageDescription=false] - Whether package description is required
 */

/**
 * Package item component for multi-package modal
 * @param {PackageItemProps} props
 * @returns {React.ReactElement}
 */
export const PackageItem = ({
  changeInsuranceValue,
  changePackageDimensions,
  changePackageContentDescription,
  changeWeight,
  duplicatePackage,
  hasInsuranceProviderSelected = false,
  index = 0,
  pack,
  readOnly = false,
  removePackage,
  shouldRequirePackageDescription = false,
}) => {
  // Get custom packages from Redux
  const allPackages = useSelector(Global.selectSellerPackages);

  // Transform packages to dropdown options
  const packageOptions = useMemo(() => {
    const customPackages = allPackages.filter((pkg) => pkg.isCustom && !pkg.isHidden);
    return customPackages.map((pkg) => ({
      label: pkg.name,
      value: pkg.packageTypeId,
    }));
  }, [allPackages]);

  // Local state for selected package type
  const [selectedPackageTypeId, setSelectedPackageTypeId] = useState(pack?.packageTypeId ?? '');

  // Handler for package type selection
  const handlePackageTypeSelect = useCallback(
    (packageTypeId) => {
      const selectedPackage = allPackages.find((pkg) => pkg.packageTypeId === packageTypeId);
      if (selectedPackage) {
        // Update dimensions with selected package dimensions
        changePackageDimensions(selectedPackage.dimensions, index);

        // Update local state
        setSelectedPackageTypeId(packageTypeId);

        // Track segment event
        Segment.trackEvent(MULTI_PACKAGE_CUSTOM_PACKAGE_TYPE_SELECTED, {
          type: MULTI_PACKAGE_CUSTOM_PACKAGE_TYPE_SELECTED,
          packageTypeId: selectedPackage.packageTypeId,
          packageName: selectedPackage.name,
          packageIndex: index,
        });
      }
    },
    [allPackages, changePackageDimensions, index],
  );

  const changePackageDimensionsCb = useCallback(
    (inputDimensions) => {
      changePackageDimensions(inputDimensions, index);
    },
    [changePackageDimensions, index],
  );

  const changeWeightCb = useCallback(
    (formWeight, weightInputUnit) => {
      const weight = {
        unit: convertToWeightUnitEnum(weightInputUnit),
        value: formWeight,
      };
      changeWeight(weight, index);
    },
    [changeWeight, index],
  );

  const duplicatePackageCb = useCallback(() => {
    duplicatePackage(pack);
  }, [duplicatePackage, pack]);

  const removePackageCb = useCallback(() => {
    removePackage(index);
  }, [removePackage, index]);

  const changeInsuranceValueCb = useCallback(
    (insuredAmount, currencyCode) => {
      changeInsuranceValue(insuredAmount, currencyCode, index);
    },
    [changeInsuranceValue, index],
  );

  const onScaleReadCb = useCallback(
    (result) => {
      changeWeight(result.weight, index);
      if (result.dims) {
        changePackageDimensions(result.dims, index);
      }
    },
    [changeWeight, changePackageDimensions, index],
  );
  const onReadFromScale = useReadFromScale(onScaleReadCb);

  const onUpdatePackageContentDescription = useCallback(
    (updatedPackageDescription) => {
      changePackageContentDescription(updatedPackageDescription, index);
    },
    [changePackageContentDescription, index],
  );

  const classes = classNames(style.package, { [style['read-only']]: readOnly });
  const elementId = 'multi-package-';

  if (readOnly) {
    return (
      <div className={classes}>
        <div className={style.header}>
          <div className={style.title}>
            <FormattedMessage {...messages.packageCount} values={{ count: index + 1 }} />
          </div>
        </div>
        <div className={style.body}>
          <PackageItemRow content={formatDimensions(pack.dimensions)} label={<DimensionsLabel />} />
          <PackageItemRow content={formatWeight(pack.weight, pack.weight?.unit)} label={<WeightLabel />} />
          {shouldRequirePackageDescription ? (
            <PackageItemRow content={pack.contentDescription} label={<PackageContentDescriptionLabel />} />
          ) : null}
          <PackageItemRow content={formatCurrency(pack.insuredValue)} label={<InsuredAmountLabel />} />
        </div>
      </div>
    );
  }

  return (
    <div className={style.package}>
      <div className={style.header}>
        <div className={style.title}>
          <FormattedMessage {...messages.packageCount} values={{ count: index + 1 }} />
        </div>
        <div className={style.links}>
          <Button onClick={duplicatePackageCb} theme='link'>
            <FormattedMessage {...messages.duplicate} />
          </Button>
          <div className={style['spacer-bar']}>&nbsp;</div>
          <Button onClick={removePackageCb} theme='danger-link'>
            <FormattedMessage {...messages.delete} />
          </Button>
        </div>
      </div>
      <section className={style.body}>
        {packageOptions.length > 0 && (
          <PackageItemRow
            content={
              <SimpleDropdown
                className={style['package-type-dropdown']}
                label=''
                onItemSelected={handlePackageTypeSelect}
                options={packageOptions}
                placeholder={messages.customPackageTypePlaceholder.defaultMessage}
                selectedItemValue={selectedPackageTypeId}
              />
            }
            label={<PackageTypeLabel />}
            labelId={`${elementId}package-type-${index}`}
          />
        )}
        <PackageItemRow
          content={
            <DimensionInput
              dimensions={get(pack, 'dimensions')}
              labelId={`${elementId}${CSWFieldId.Dimension}${index}`}
              onChange={changePackageDimensionsCb}
              unit={convertFromDimensionUnitEnum(get(pack, 'dimensions.unit'))}
            />
          }
          label={<DimensionsLabel />}
          labelId={`${elementId}${CSWFieldId.Dimension}${index}`}
        />
        <div>
          <PackageItemRow
            content={
              <WeightInput
                className={WeightUnit.Grams === get(pack, 'weight.unit') ? style['weight-grams'] : style['weight-ounces']}
                labelId={`${elementId}${CSWFieldId.Weight}${index}`}
                onChange={changeWeightCb}
                unit={convertFromWeightUnitEnum(get(pack, 'weight.unit'))}
                weight={toFixedNumber(get(pack, 'weight.value', '0'), 3)}
              />
            }
            label={<WeightLabel />}
            labelId={`${elementId}${CSWFieldId.Weight}${index}`}
          />
          <Scale className={style.scale} onReadFromScale={onReadFromScale} />
        </div>
        {shouldRequirePackageDescription ? (
          <PackageItemRow
            className={style['package-description']}
            content={
              <PackageContentDescriptionInputForMultiPackage
                id={`${elementId}${CSWFieldId.PackageDescription}${index}`}
                initialValue={pack.contentDescription ?? ''}
                onBlur={onUpdatePackageContentDescription}
              />
            }
            label={<PackageContentDescriptionLabel />}
            labelId={`${elementId}${CSWFieldId.PackageDescription}${index}`}
          />
        ) : null}
        <PackageItemRow
          content={
            <PackageInsurance
              hasInsuranceProviderSelected={hasInsuranceProviderSelected}
              insuredValue={pack.insuredValue}
              labelId={`${elementId}${CSWFieldId.InsuranceAmount}${index}`}
              onChange={changeInsuranceValueCb}
            />
          }
          label={<InsuredAmountLabel />}
          labelId={`${elementId}${CSWFieldId.InsuranceAmount}${index}`}
        />
      </section>
    </div>
  );
};
